<template>
  <div class="chat-page">
    <div class="chat-container" :class="{ 'mobile-chat-active': currentRoom !== null }">
      <!-- Sidebar: Rooms List -->
      <div class="chat-sidebar">
        <div class="sidebar-header">
          <div class="header-main">
            <h3>消息列表</h3>
            <div class="ws-status-badge" :class="{ connected: wsConnected }" :title="wsConnected ? '网络良好' : '正在连接...'">
              <span class="status-dot"></span>
              {{ wsConnected ? '已连接' : '连接中' }}
            </div>
          </div>
          <Button type="primary" shape="circle" icon="md-add" @click="showNewChat = true"></Button>
        </div>

        <div class="search-box">
          <Input prefix="ios-search" placeholder="搜索聊天..." v-model="searchQuery" />
        </div>

        <div class="room-list-wrapper">
          <div v-for="room in filteredRooms" :key="room.id" class="room-item"
            :class="{ active: currentRoom && currentRoom.id === room.id }" @click="selectRoom(room)">
            <div class="room-avatar">
              <Badge :count="room.unreadCount" :offset="[0, 0]">
                <Avatar :src="getRoomAvatar(room)" size="large" />
              </Badge>
              <span v-if="isRoomOnline(room)" class="online-status"></span>
            </div>
            <div class="room-info">
              <div class="room-top">
                <span class="room-name">{{ getRoomName(room) }}</span>
                <span class="room-time">{{ formatTime(room.updatedAt) }}</span>
              </div>
              <div class="room-bottom">
                <span class="room-last-msg">
                  <span v-if="room.lastMessage">{{ room.lastMessage.senderName }}: {{ room.lastMessage.content || '[文件]'
                  }}</span>
                  <span v-else>暂无消息</span>
                </span>
                <span v-if="room.isTyping" class="typing-indicator">正在输入...</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Main: Chat Window -->
      <div class="chat-main">
        <template v-if="currentRoom">
          <div class="chat-header">
            <div class="header-info">
              <Button type="text" icon="md-arrow-back" class="mobile-back-btn" @click="currentRoom = null" />
              <Avatar :src="getRoomAvatar(currentRoom)" size="default" />
              <div class="header-text">
                <h4>{{ getRoomName(currentRoom) }}</h4>
                <p v-if="currentRoom.type === 'private'">{{ isRoomOnline(currentRoom) ? '在线' : '离线' }}</p>
                <p v-else>{{ currentRoom.Members ? currentRoom.Members.length : 0 }} 位成员</p>
              </div>
            </div>
            <div class="header-actions">
              <Button type="text" icon="md-more"></Button>
            </div>
          </div>

          <div class="message-list" ref="messageList">
            <div v-if="hasMore" class="load-more">
              <Button type="text" :loading="loadingMore" @click="loadMoreMessages">加载更多</Button>
            </div>

            <div v-for="(msg, index) in messages" :key="msg.id || index" class="message-wrapper"
              :class="{ 'message-mine': msg.senderId === currentUser.id }">
              <div v-if="showTime(index)" class="message-time-sep">
                {{ formatFullTime(msg.createdAt) }}
              </div>

              <div class="message-content-row">
                <Avatar :src="msg.avatar || defaultAvatar" class="msg-avatar" size="small" />
                <div class="msg-bubble-container">
                  <div class="msg-sender-name" v-if="currentRoom.type === 'group' && msg.senderId !== currentUser.id">
                    {{ msg.senderName }}
                  </div>
                  <div class="msg-bubble" :class="{
                    ['msg-type-' + msg.msgType]: true,
                    'msg-type-image': isImageFile(msg.fileName)
                  }">
                    <template v-if="msg.msgType === 'text'">
                      {{ msg.content }}
                    </template>
                    <template v-else-if="msg.msgType === 'image' || isImageFile(msg.fileName)">
                      <img :src="msg.fileUrl.replace(/ /g, '%20')" class="msg-image"
                        @click="previewImage(msg.fileUrl)" />
                    </template>
                    <template v-else-if="msg.msgType === 'file'">
                      <div class="msg-file" @click="downloadFile(msg.fileUrl)">
                        <Icon type="md-document" size="24" />
                        <div class="file-info">
                          <div class="file-name">{{ msg.fileName }}</div>
                          <div class="file-action">点击下载</div>
                        </div>
                      </div>
                    </template>
                  </div>
                  <div v-if="msg.senderId === currentUser.id" class="msg-status-outer">
                    <Icon v-if="isMessageRead(msg)" type="md-done-all" class="read-icon" />
                    <Icon v-else type="md-checkmark" class="sent-icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="chat-input-area">
            <div class="input-toolbar">
              <Upload action="/api/chat/upload" :headers="uploadHeaders" :show-upload-list="false"
                :on-success="handleUploadSuccess" :on-error="handleUploadError" style="display: inline-block;"
                accept="image/*" :data="{ roomID: currentRoom.id }">
                <Button type="text" icon="md-image" title="图片"></Button>
              </Upload>
              <Upload action="/api/chat/upload" :headers="uploadHeaders" :show-upload-list="false"
                :on-success="handleFileUploadSuccess" :on-error="handleUploadError" style="display: inline-block;"
                :data="{ roomID: currentRoom.id }">
                <Button type="text" icon="md-folder" title="文件"></Button>
              </Upload>
              <Poptip placement="top-start" width="300" transfer>
                <Button type="text" icon="md-happy" title="表情"></Button>
                <template #content>
                  <div class="emoji-picker">
                    <span v-for="emoji in commonEmojis" :key="emoji" class="emoji-item" @click="addEmoji(emoji)">
                      {{ emoji }}
                    </span>
                  </div>
                </template>
              </Poptip>
            </div>
            <div class="input-box">
              <Input v-model="inputMsg" type="textarea" :autosize="{ minRows: 1, maxRows: 4 }" placeholder="键入消息..."
                @on-enter="sendMessage" @on-change="handleInput" />
              <Button type="primary" :disabled="!inputMsg.trim()" @click="sendMessage">发送</Button>
            </div>
          </div>
        </template>

        <div v-else class="chat-empty">
          <Icon type="ios-chatbubbles-outline" size="100" color="#e8eaec" />
          <p>请从左侧选择一个聊天，或者开启新会话</p>
          <Button type="primary" ghost @click="showNewChat = true">开启新会话</Button>
        </div>
      </div>
    </div>

    <!-- New Chat Modal -->
    <Modal v-model="showNewChat" title="搜索添加好友" :footer-hide="true" width="500">
      <div class="user-search-modal">
        <div class="search-input-group">
          <Input v-model="userSearchQuery" placeholder="通过用户名或ID搜索..." size="large" search enter-button="搜索"
            @on-search="handleUserSearch" :loading="searchingUsers" />
        </div>

        <div class="search-results-list">
          <div v-if="searchingUsers" class="search-loading">
            <Spin size="large"></Spin>
            <p>正在搜索...</p>
          </div>
          <template v-else-if="userSearchResults.length > 0">
            <div v-for="user in userSearchResults" :key="user.id" class="search-result-item">
              <Avatar :src="user.avatar || defaultAvatar" size="large" />
              <div class="result-info">
                <div class="result-name">{{ user.username }}</div>
                <div class="result-id">ID: {{ user.id }}</div>
              </div>
              <Button type="primary" size="small" @click="startChatWithUser(user.id)">开启会话</Button>
            </div>
          </template>
          <div v-else-if="userSearchQuery" class="search-no-results">
            <Icon type="ios-search" size="40" color="#ccc" />
            <p>未找到匹配的用户</p>
          </div>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/zh-cn';
import api from '@/utils/api';

dayjs.extend(relativeTime);
dayjs.locale('zh-cn');

export default {
  name: 'Chat',
  data() {
    return {
      ws: null,
      wsConnected: false,
      reconnectTimer: null,
      reconnectCount: 0,
      maxReconnectAttempts: 10,
      rooms: [],
      messages: [],
      roomMembers: [],
      currentRoom: null,
      searchQuery: '',
      inputMsg: '',
      showNewChat: false,
      selectedUser: null,
      allUsers: [],
      onlineUserIds: [],
      loadingMore: false,
      hasMore: false,
      page: 1,
      creatingChat: false,
      defaultAvatar: 'https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png',
      typingTimers: {},
      userSearchQuery: '',
      userSearchResults: [],
      searchingUsers: false,
      isKicked: false,
      lastKickTime: 0,
      reconnectStableTimer: null,
      commonEmojis: [
        '😀', '😁', '😂', '🤣', '😃', '😄', '😅', '😆', '😉', '😊', '😋', '😎', '😍', '😘', '🥰', '😗', '😙', '😚', '☺️', '🙂', '🤗', '🤩',
        '🤔', '🤨', '😐', '😑', '😶', '🙄', '😏', '😣', '😥', '😮', '🤐', '😯', '😪', '😫', '😴', '😌', '😛', '😜', '😝', '🤤', '😒', '😓',
        '😔', '😕', '🙃', '🤑', '😲', '☹️', '🙁', '😖', '😞', '😟', '😤', '😢', '😭', '😦', '😧', '😨', '😩', '🤯', '😬', '😰', '😱', '🥵',
        '🥶', '😳', '🤪', '😵', '😡', '😠', '🤬', '😷', '🤒', '🤕', '🤢', '🤮', '🤧', '😇', '🤠', '🤡', '🥳', '🥴', '🥺', '🤥', '🤫', '🤭',
        '🧐', '🤓', '😈', '👿', '👹', '👺', '💀', '👻', '👽', '🤖', '💩', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾', '👋', '🤚',
        '🖐️', '✋', '🖖', '👌', '🤏', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉', '👆', '🖕', '👇', '☝️', '👍', '👎', '✊', '👊', '🤛', '🤜',
        '👏', '🙌', '👐', '🤲', '🤝', '🙏', '✍️', '💅', '🤳', '💪', '🦾', '🦵', '🦿', '🦶', '👂', '🦻', '👃', '🧠', '🦷', '🦴', '👀', '👁️',
        '👅', '👄', '💋', '🩸'
      ]
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.user || {};
    },
    filteredRooms() {
      if (!this.searchQuery) return this.rooms;
      const q = this.searchQuery.toLowerCase();
      return this.rooms.filter(r => this.getRoomName(r).toLowerCase().includes(q));
    },
    uploadHeaders() {
      return { Authorization: `Bearer ${this.$store.state.token}` };
    }
  },
  created() {
    this.fetchRooms();
    this.initWebSocket();
    this.fetchAllUsers();
  },
  beforeUnmount() {
    this.clearWs();
  },
  methods: {
    clearWs() {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      if (this.reconnectStableTimer) {
        clearTimeout(this.reconnectStableTimer);
        this.reconnectStableTimer = null;
      }
      if (this.ws) {
        this.ws.onopen = null;
        this.ws.onmessage = null;
        this.ws.onerror = null;
        this.ws.onclose = null;
        this.ws.close();
        this.ws = null;
      }
      this.wsConnected = false;
    },
    initWebSocket() {
      if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
        return;
      }
      if (this.reconnectCount > this.maxReconnectAttempts) {
        console.warn('Max WS reconnection attempts reached');
        return;
      }

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host === 'localhost:8080' ? 'localhost:8081' : window.location.host;
      if (!this.$store.state.token) return;

      const wsUrl = `${protocol}//${host}/api/chat/ws?token=${this.$store.state.token}`;

      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log('WS Connection established');
        this.wsConnected = true;
        this.isKicked = false;

        // Reset reconnect count only after connection has been stable for 5 seconds
        if (this.reconnectStableTimer) clearTimeout(this.reconnectStableTimer);
        this.reconnectStableTimer = setTimeout(() => {
          this.reconnectCount = 0;
          console.log('WS connection stable, reset reconnectCount');
        }, 5000);
      };

      this.ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        this.handleWsMessage(msg);
      };

      this.ws.onerror = (err) => {
        console.error('WS Error:', err);
      };

      this.ws.onclose = (event) => {
        console.log('WS Connection closed', event.code, event.reason);
        this.wsConnected = false;

        // Prevent multiple timers if onclose is somehow triggered twice
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

        // Don't reconnect if kicked out (4001) or explicitly closed normally
        if (event.code === 4001 || this.isKicked || (Date.now() - this.lastKickTime < 10000)) {
          this.isKicked = true;
          this.lastKickTime = Date.now();
          this.$Modal.warning({
            title: '连接已断开',
            content: '您的账号在其他地方登录，当前连接已被断开。',
            okText: '确定'
          });
          console.log('WS: Connection closed because of kick or recent kick, skipping reconnect');
          return;
        }

        if (this.reconnectCount < this.maxReconnectAttempts) {
          // Exponential backoff + jitter (0-2000ms), starting at 3s
          const delay = Math.min(3000 * Math.pow(2, this.reconnectCount), 30000) + Math.random() * 2000;
          this.reconnectCount++;
          console.log(`Scheduling WS reconnect in ${Math.round(delay)}ms (attempt ${this.reconnectCount})`);
          this.reconnectTimer = setTimeout(() => this.initWebSocket(), delay);
        } else {
          this.$Message.error('连接已断开，请刷新页面重新连接');
        }
      };
    },
    handleWsMessage(msg) {
      console.log('WS: Received message type:', msg.type);
      switch (msg.type) {
        case 'kick':
          console.log('WS: Received kick message');
          this.isKicked = true;
          this.lastKickTime = Date.now();
          this.$Modal.warning({
            title: '连接已断开',
            content: '您的账号在其他地方登录，当前连接已被断开。',
            okText: '确定'
          });
          this.clearWs();
          break;
        case 'chat':
          this.onReceiveMessage(msg.payload);
          break;
        case 'read':
          this.onReceiveRead(msg.payload);
          break;
        case 'online':
          this.onlineUserIds = msg.payload.onlineUserIds;
          break;
        case 'typing':
          this.onReceiveTyping(msg.payload);
          break;
      }
    },
    onReceiveMessage(payload) {
      // If current room, add to list
      if (this.currentRoom && this.currentRoom.id === payload.roomId) {
        this.messages.push(payload);
        this.scrollToBottom();
        // Mark as read
        this.markRoomRead(payload.roomId);
      }

      // Update room last message and count
      const room = this.rooms.find(r => r.id === payload.roomId);
      if (room) {
        room.lastMessage = {
          content: payload.content,
          msgType: payload.msgType,
          senderName: payload.senderName,
          createdAt: payload.createdAt
        };
        room.updatedAt = payload.createdAt;
        if (!this.currentRoom || this.currentRoom.id !== payload.roomId) {
          room.unreadCount++;
        }
        // Move to top
        this.rooms.sort((a, b) => b.updatedAt - a.updatedAt);
      } else {
        // Room not in list, fetch rooms again
        this.fetchRooms();
      }
    },
    onReceiveTyping(payload) {
      const room = this.rooms.find(r => r.id === payload.roomId);
      if (room) {
        room.isTyping = true;
        if (this.typingTimers[room.id]) clearTimeout(this.typingTimers[room.id]);
        this.typingTimers[room.id] = setTimeout(() => {
          room.isTyping = false;
        }, 3000);
      }
    },
    async fetchRooms() {
      try {
        const res = await api.get('/api/chat/rooms');
        const data = res.data;
        this.rooms = (data.rooms || []).map(r => ({ ...r, isTyping: false }));
      } catch (e) {
        if (e.response && e.response.data && e.response.data.code === 'USER_BANNED') {
          // Let the global interceptor handle the redirect
          return;
        }
        this.$Message.error('获取聊天列表失败');
      }
    },
    async selectRoom(room) {
      this.currentRoom = room;
      this.page = 1;
      this.messages = [];
      this.hasMore = false;
      await this.fetchMessages(room.id);
      this.markRoomRead(room.id);
      room.unreadCount = 0;
      this.scrollToBottom();
    },
    async fetchMessages(roomId, loadMore = false) {
      try {
        const res = await api.get(`/api/chat/rooms/${roomId}/messages?page=${this.page}&pageSize=50`);
        const data = res.data;
        if (loadMore) {
          this.messages = [...data.messages, ...this.messages];
        } else {
          this.messages = data.messages || [];
          this.roomMembers = data.members || [];
        }
        this.hasMore = this.messages.length < data.total;
      } catch (e) {
        if (e.response && e.response.data && e.response.data.code === 'USER_BANNED') return;
        this.$Message.error('获取消息失败');
      }
    },
    onReceiveRead(payload) {
      if (this.currentRoom && this.currentRoom.id === payload.roomId) {
        const member = this.roomMembers.find(m => m.userId === payload.userId);
        if (member) {
          member.lastReadAt = payload.lastReadAt;
        }
      }
    },
    isImageFile(fileName) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(fileName || '');
    },
    isMessageRead(msg) {
      if (!this.roomMembers || this.roomMembers.length === 0) return false;
      // In private chat, we check if the peer has read it. 
      // In group chat, Telegram shows double tick if anyone has read it.
      return this.roomMembers.some(m => m.userId !== this.currentUser.id && m.lastReadAt >= msg.createdAt);
    },
    async loadMoreMessages() {
      if (!this.hasMore || this.loadingMore) return;
      this.loadingMore = true;
      this.page++;
      const oldScrollHeight = this.$refs.messageList.scrollHeight;
      await this.fetchMessages(this.currentRoom.id, true);
      this.loadingMore = false;
      this.$nextTick(() => {
        this.$refs.messageList.scrollTop = this.$refs.messageList.scrollHeight - oldScrollHeight;
      });
    },
    sendMessage() {
      if (!this.inputMsg.trim()) return;

      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
        this.$Message.warning('正在连接服务器，请稍后再试...');
        if (!this.ws || this.ws.readyState === WebSocket.CLOSED) {
          this.initWebSocket();
        }
        return;
      }

      const payload = {
        roomId: this.currentRoom.id,
        msgType: 'text',
        content: this.inputMsg.trim()
      };

      this.ws.send(JSON.stringify({
        type: 'chat',
        payload: payload
      }));

      this.inputMsg = '';
    },
    handleInput() {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.currentRoom) return;
      this.ws.send(JSON.stringify({
        type: 'typing',
        payload: { roomId: this.currentRoom.id }
      }));
    },
    async markRoomRead(roomId) {
      try {
        await api.put(`/api/chat/rooms/${roomId}/read`);
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({
            type: 'read',
            payload: { roomId }
          }));
        }
      } catch (e) { }
    },
    handleUploadSuccess(res) {
      if (!this.ws || !this.currentRoom) return;
      const payload = {
        roomId: this.currentRoom.id,
        msgType: 'image',
        fileUrl: res.url,
        fileName: res.fileName
      };
      this.ws.send(JSON.stringify({
        type: 'chat',
        payload: payload
      }));
    },
    handleFileUploadSuccess(res) {
      if (!this.ws || !this.currentRoom) return;

      // Ensure images uploaded via "File" button are still rendered natively as images
      const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(res.fileName || '');

      const payload = {
        roomId: this.currentRoom.id,
        msgType: isImage ? 'image' : 'file',
        fileUrl: res.url,
        fileName: res.fileName
      };
      this.ws.send(JSON.stringify({
        type: 'chat',
        payload: payload
      }));
    },
    handleUploadError() {
      this.$Message.error('上传失败');
    },
    async handleUserSearch() {
      if (!this.userSearchQuery.trim()) return;
      this.searchingUsers = true;
      try {
        const res = await api.get(`/api/chat/users?query=${encodeURIComponent(this.userSearchQuery)}`);
        const data = res.data;
        this.userSearchResults = data.users || [];
      } catch (e) {
        if (e.response && e.response.data && e.response.data.code === 'USER_BANNED') return;
        this.$Message.error('搜索失败');
      } finally {
        this.searchingUsers = false;
      }
    },
    async startChatWithUser(userId) {
      this.creatingChat = true;
      try {
        const res = await api.post('/api/chat/rooms', {
          targetUserId: userId,
          type: 'private'
        });
        const data = res.data;
        const room = { ...data.room, isTyping: false };
        if (!this.rooms.find(r => r.id === room.id)) {
          this.rooms.unshift(room);
        }
        this.selectRoom(room);
        this.showNewChat = false;
      } catch (e) {
        if (e.response && e.response.data && e.response.data.code === 'USER_BANNED') return;
        this.$Message.error(e.response?.data?.error || '创建会话失败');
      } finally {
        this.creatingChat = false;
      }
    },
    async fetchAllUsers() {
      // Keep this for completeness but we mostly use search now
      try {
        const res = await api.get('/api/chat/users');
        const data = res.data;
        this.allUsers = data.users || [];
      } catch (e) { }
    },
    // Removed old startNewChat in favor of startChatWithUser
    getRoomName(room) {
      if (room.type === 'group') return room.name;
      const other = room.members ? room.members.find(m => m.userId !== this.currentUser.id) : null;
      return other ? other.username : '未知用户';
    },
    getRoomAvatar(room) {
      if (room.type === 'group') return this.defaultAvatar;
      const other = room.members ? room.members.find(m => m.userId !== this.currentUser.id) : null;
      return other ? (other.avatar || this.defaultAvatar) : this.defaultAvatar;
    },
    isRoomOnline(room) {
      if (room.type === 'group') return false;
      const other = room.members ? room.members.find(m => m.userId !== this.currentUser.id) : null;
      return other ? this.onlineUserIds.includes(other.userId) : false;
    },
    formatTime(ts) {
      if (!ts) return '';
      const d = dayjs(ts * 1000);
      if (dayjs().isSame(d, 'day')) return d.format('HH:mm');
      if (dayjs().isSame(d, 'year')) return d.format('MM-DD');
      return d.format('YYYY-MM-DD');
    },
    formatFullTime(ts) {
      return dayjs(ts * 1000).format('YYYY-MM-DD HH:mm');
    },
    showTime(index) {
      if (index === 0) return true;
      const prev = this.messages[index - 1];
      const curr = this.messages[index];
      return curr.createdAt - prev.createdAt > 300; // 5 mins
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messageList;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    previewImage(url) {
      window.open(url, '_blank');
    },
    downloadFile(url) {
      window.open(url, '_blank');
    },
    addEmoji(emoji) {
      this.inputMsg += emoji;
    }
  }
};
</script>

<style scoped>
.chat-page {
  width: 100%;
  min-height: calc(100vh - 64px);
  background: #f0f2f5;
  padding: 24px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.chat-container {
  width: 100%;
  max-width: 1400px;
  height: calc(100vh - 112px);
  /* 64px navbar + 48px vertical padding */
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.05);
  display: flex;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.02);
}

/* Sidebar */
.chat-sidebar {
  width: 320px;
  border-right: 1px solid #efefef;
  display: flex;
  flex-direction: column;
}

.sidebar-header {
  padding: 16px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f2f5;
}

.header-main {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ws-status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #fa8c16;
  background: #fff7e6;
  padding: 2px 8px;
  border-radius: 10px;
  width: fit-content;
  transition: all 0.3s;
}

.ws-status-badge.connected {
  color: #52c41a;
  background: #f6ffed;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #fa8c16;
  box-shadow: 0 0 5px #fa8c16;
}

.ws-status-badge.connected .status-dot {
  background: #52c41a;
  box-shadow: 0 0 5px #52c41a;
}

.sidebar-header h3 {
  font-size: 18px;
  color: #1a1a1a;
}

.search-box {
  padding: 12px 20px;
}

.room-list-wrapper {
  flex: 1;
  overflow-y: auto;
}

.room-item {
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.room-item:hover {
  background: #f9f9f9;
}

.room-item.active {
  background: #e6f7ff;
}

.room-avatar {
  position: relative;
}

.online-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 12px;
  height: 12px;
  background: #52c41a;
  border-radius: 50%;
  border: 2px solid #fff;
}

.room-info {
  flex: 1;
  min-width: 0;
}

.room-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.room-name {
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.room-time {
  font-size: 12px;
  color: #999;
}

.room-bottom {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
}

.room-last-msg {
  color: #666;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

.typing-indicator {
  color: #1890ff;
  font-style: italic;
}

/* Main Chat Area */
.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #fff;
  position: relative;
}

.chat-header {
  padding: 12px 24px;
  border-bottom: 1px solid #f5f5f5;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-text h4 {
  font-size: 16px;
  margin: 0;
}

.header-text p {
  font-size: 12px;
  color: #999;
  margin: 0;
}

.ws-status {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #faad14;
  margin-top: 2px;
}

.ws-status.connected {
  color: #52c41a;
}

.ws-status .status-dot {
  width: 6px;
  height: 6px;
  background: currentColor;
  border-radius: 50%;
}

.ws-status:not(.connected) .status-dot {
  animation: pulse-ws 1.5s infinite;
}

@keyframes pulse-ws {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }

  100% {
    opacity: 1;
  }
}

.message-list {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.load-more {
  text-align: center;
  margin-bottom: 10px;
}

.message-wrapper {
  display: flex;
  flex-direction: column;
}

.message-time-sep {
  text-align: center;
  font-size: 12px;
  color: #bfbfbf;
  margin: 16px 0;
}

.message-content-row {
  display: flex;
  gap: 10px;
  max-width: 80%;
}

.message-mine {
  align-items: flex-end;
}

.message-mine .message-content-row {
  flex-direction: row-reverse;
}

.msg-bubble-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.msg-sender-name {
  font-size: 12px;
  color: #8c8c8c;
}

.msg-bubble {
  padding: 10px 14px;
  padding-bottom: 18px;
  /* Extra space for ticks */
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.5;
  position: relative;
  min-width: 60px;
  word-break: break-all;
}

.msg-bubble-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
}

.msg-status-outer {
  position: absolute;
  bottom: 0px;
  right: -24px;
  display: flex;
  align-items: center;
}

.read-icon {
  color: #52c41a;
  /* Green for read */
  font-size: 16px;
}

.sent-icon {
  color: #bfbfbf;
  font-size: 16px;
}

.message-mine .msg-bubble {
  background: #1890ff;
  color: #fff;
  border-top-right-radius: 2px;
}

.message-wrapper:not(.message-mine) .msg-bubble {
  background: #fff;
  color: #333;
  border: 1px solid #f0f0f0;
  border-top-left-radius: 2px;
}

.msg-status {
  display: none;
  /* Old status inside bubble */
}

.msg-bubble.msg-type-image,
.msg-bubble.msg-type-file {
  padding: 4px;
  background: transparent !important;
  border: none !important;
  box-shadow: none !important;
}

.msg-bubble.msg-type-image .msg-status {
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 4px;
  border-radius: 4px;
  bottom: 4px;
  right: 4px;
}

.message-wrapper:not(.message-mine) .msg-bubble.msg-type-image .msg-status {
  display: none;
  /* Not needed for received messages status */
}

.msg-image {
  max-width: 240px;
  max-height: 320px;
  border-radius: 6px;
  cursor: pointer;
  display: block;
}

.msg-file {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  padding: 10px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  cursor: pointer;
  color: #333;
}

.file-info {
  flex: 1;
  min-width: 0;
}

.file-name {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-action {
  font-size: 11px;
  color: #1890ff;
}

/* Input Area */
.chat-input-area {
  padding: 16px 24px;
  border-top: 1px solid #f5f5f5;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.input-toolbar {
  display: flex;
  gap: 4px;
}

.input-box {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.chat-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;
  color: #999;
}

.emoji-picker {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 8px;
  padding: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.emoji-item {
  font-size: 20px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.2s;
  padding: 4px;
  border-radius: 4px;
}

.emoji-item:hover {
  background: #f5f5f5;
  transform: scale(1.2);
}

/* Scrollbar for emoji picker */
.emoji-picker::-webkit-scrollbar {
  width: 4px;
}

.emoji-picker::-webkit-scrollbar-thumb {
  background: #dadada;
  border-radius: 2px;
}

/* New Chat Modal */
.user-search-modal {
  padding: 10px 0;
}

.search-results-list {
  margin-top: 20px;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
}

.search-loading,
.search-no-results {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #999;
}

.search-loading p,
.search-no-results p {
  margin-top: 10px;
}

.search-result-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-bottom: 1px solid #f5f5f5;
  gap: 12px;
}

.search-result-item:last-child {
  border-bottom: none;
}

.result-info {
  flex: 1;
}

.result-name {
  font-weight: 600;
  color: #333;
}

.result-id {
  font-size: 12px;
  color: #999;
}

/* Scrollbar */
.room-list-wrapper::-webkit-scrollbar,
.message-list::-webkit-scrollbar {
  width: 6px;
}

.room-list-wrapper::-webkit-scrollbar-thumb,
.message-list::-webkit-scrollbar-thumb {
  background: #e8e8e8;
  border-radius: 3px;
}

.room-list-wrapper::-webkit-scrollbar-track,
.message-list::-webkit-scrollbar-track {
  background: transparent;
}

/* Mobile Responsive Layout */
.mobile-back-btn {
  display: none;
}

@media screen and (max-width: 768px) {
  .chat-page {
    padding: 0;
    min-height: 100vh;
  }

  .chat-container {
    height: calc(100vh - 64px);
    /* assuming navbar is 64px */
    border-radius: 0;
    border: none;
    box-shadow: none;
  }

  .chat-sidebar {
    width: 100%;
    border-right: none;
  }

  .chat-main {
    display: none;
  }

  .chat-container.mobile-chat-active .chat-sidebar {
    display: none;
  }

  .chat-container.mobile-chat-active .chat-main {
    display: flex;
    width: 100%;
  }

  .mobile-back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 8px;
    padding: 0 4px;
    font-size: 20px;
    color: #666;
  }

  .message-input {
    min-height: 40px;
  }

  .chat-input-area {
    padding: 10px 16px;
  }

  .message-content-row {
    max-width: 95%;
  }
}
</style>
